import { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';

import { CompactPageLayout } from 'common/components/compactPageLayout';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Utils from 'common/services/Utils';
import { LOGGER_LOG_TYPE } from 'Config';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { ReactSpreadsheetImport } from 'common/components/spreadsheet/ReactSpreadsheetImport';
import { Data, Result } from 'common/components/spreadsheet/types';
import { MULTI_VALUE_SEPARATOR, NumericKind } from 'common/components/spreadsheet/types';
import { formatNumericText, parseNumericText } from 'common/components/spreadsheet/cellValues';

import { ContractModel } from 'api/contracts/enums/ContractModel';
import BulkContractsService from 'api/contracts/bulk/BulkContractsService';
import { BulkContractExistingValues, BulkContractRow, BulkContractsCommitResult } from 'api/contracts/bulk/models/BulkContractsModels';
import SuppliersService from 'api/suppliers/SuppliersService';
import { CatalogSupplierSearchCriteria } from 'api/catalog/models/CatalogSupplierSearchCriteria';
import VehiclesService from 'api/vehicles/VehiclesService';
import UseContractsService from 'api/contracts/useContract/UseContractsService';
import InsuranceContractsService from 'api/contracts/insuranceContract/InsuranceContractsService';
import MaintenanceContractsService from 'api/contracts/maintenanceContract/MaintenanceContractsService';

import ContractFamilyStep from './components/ContractFamilyStep';
import ReplicateRowsButton from './components/ReplicateRowsButton';
import BulkContractsResultPanel from './components/BulkContractsResultPanel';
import {
    ContractFieldOptions, DATE_CELL_FORMAT, buildContractFields, buildContractRowHook,
    lockedUseContractFields, useContractTypeNameOf,
} from './contractFields';
import styles from './BulkContractsScreen.module.scss';

const BLANK_ROWS = 25;

const SUPPLIER_CONTRACT_TYPE: Record<ContractModel, string> = {
    [ContractModel.USE]: 'RENTAL',
    [ContractModel.MAINTENANCE]: 'MAINTENANCE',
    [ContractModel.INSURANCE]: 'INSURANCE',
};

const toNumber = (value: unknown) => {
    if (value === undefined || value === null || value === '') return null;
    // Mesma leitura que a grelha usa a escrever a celula — o parse generico da app le "25.400" como
    // 25,4 e a base de dados ficava com um numero diferente do que estava no ecra.
    const parsed = parseNumericText(String(value));
    return parsed === null || isNaN(parsed) ? null : parsed;
};

const toIsoDate = (value: unknown) => {
    if (!value) return null;
    return Utils.parseToDate(value, DATE_CELL_FORMAT) ?? null;
};

const toText = (value: unknown) => {
    const text = ((value ?? '') as string).toString().trim();
    return text.length ? text : null;
};

// O contrato que já existe entra na grelha escrito como a grelha escreve os números — senão a linha
// que veio da base de dados e a linha colada do Excel ficavam com aspetos diferentes.
const cellNumber = (value: number | null | undefined, kind: NumericKind) =>
    (value === undefined || value === null ? '' : formatNumericText(value, kind));

const cellDate = (value?: string | null) => (value ? moment(value).format(DATE_CELL_FORMAT) : '');

// Task #66347: contrato que já existe na base de dados → valores tal como a grelha os representa.
const toCellValues = (existing: BulkContractExistingValues): Record<string, string | boolean> => ({
    vehicleId: existing.vehicleId ?? '',
    number: existing.number ?? '',
    supplierId: existing.supplierId ?? '',
    periodicity: existing.periodicity ?? '',
    cost: cellNumber(existing.cost, 'money'),
    startDate: cellDate(existing.startDate),
    endDate: cellDate(existing.endDate),
    contractTypeIds: (existing.contractTypeIds ?? []).join(MULTI_VALUE_SEPARATOR),
    useContractTypeId: existing.useContractTypeId ?? '',
    entryValue: cellNumber(existing.entryValue, 'money'),
    creditDebitNote: cellNumber(existing.creditDebitNote, 'money'),
    kmContractual: cellNumber(existing.kmContractual, 'integer'),
    warrantyYears: cellNumber(existing.warrantyYears, 'integer'),
    warrantyKilometers: cellNumber(existing.warrantyKilometers, 'integer'),
    costPerKmOver: cellNumber(existing.costPerKmOver, 'money'),
    costPerKmUnder: cellNumber(existing.costPerKmUnder, 'money'),
    contractFileId: existing.contractFileId ?? '',
    kmVehicle: cellNumber(existing.kmVehicle, 'integer'),
    returnDate: cellDate(existing.returnDate),
    returnPlace: existing.returnPlace ?? '',
    // Os toggles são booleanos na célula (não texto), senão o formatter não os desenha ligados.
    stampDutyIncluded: existing.stampDutyIncluded === true,
    maintenanceContractIncluded: existing.maintenanceContractIncluded === true,
    insuranceIncluded: existing.insuranceIncluded === true,
});

const BulkContractsScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();

    const [model, setModel] = useState<ContractModel | null>(null);
    const [options, setOptions] = useState<ContractFieldOptions | null>(null);
    const [result, setResult] = useState<BulkContractsCommitResult | null>(null);
    // Task #66347: nomes por referência, para a célula mostrar o ficheiro em vez do id. A lista de
    // ids segue no commit para o servidor apagar os que não ficarem ligados a contrato nenhum.
    const [fileNamesById, setFileNamesById] = useState<Record<string, string>>({});
    const uploadedFileIdsRef = useRef<string[]>([]);

    const backToListing = useCallback(() => history.push('/contracts'), [history]);

    const backToFamilyStep = useCallback(() => { setModel(null); setOptions(null); }, []);

    // O ← recua um passo: da grelha volta à escolha do tipo de contrato, e só de lá é que sai para a
    // listagem.
    const onBackFromHeader = useCallback(() => {
        if (model) {
            backToFamilyStep();
            return;
        }
        backToListing();
    }, [model, backToFamilyStep, backToListing]);

    const loadCatalogs = useCallback(async (selected: ContractModel) => {
        try {
            Loading.show();

            const [vehicles, suppliers] = await Promise.all([
                VehiclesService.catalog(),
                SuppliersService.catalog({ contractTypeName: SUPPLIER_CONTRACT_TYPE[selected] } as CatalogSupplierSearchCriteria),
            ]);

            let contractTypes: SelectValueLabel[] = [];
            let useContractTypeNamesById: Record<string, string> | undefined;

            if (selected === ContractModel.USE) {
                const types = await UseContractsService.getUseContractTypes();
                useContractTypeNamesById = (types || []).reduce((acc, type) => {
                    acc[type.value] = type.label;
                    return acc;
                }, {} as Record<string, string>);
                contractTypes = (types || []).map(type => ({ ...type, label: t(`common.use_contract_types.${type.label}` as any) as string }));
            } else if (selected === ContractModel.INSURANCE) {
                const types = await InsuranceContractsService.getInsuranceContractTypes();
                contractTypes = (types || []).map(type => ({
                    value: type.value,
                    label: type.isSystem ? t(`common.insurance_contract_types.${type.label}` as any) as string : type.label,
                }));
            } else {
                const types = await MaintenanceContractsService.getMaintenanceContractTypes();
                contractTypes = (types || []).map(type => ({
                    value: type.value,
                    label: type.isSystem ? t(`common.maintenance_contract_type.${type.label}` as any) as string : type.label,
                }));
            }

            setOptions({
                vehicles: Utils.sortAlphabetically(vehicles || []),
                suppliers: Utils.sortAlphabetically((suppliers || []).map(x => ({ value: x.value || '', label: x.label || '' }))),
                contractTypes: Utils.sortAlphabetically(contractTypes),
                useContractTypeNamesById,
            });
            setModel(selected);
        } catch (error) {
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.CONTRACTS, 'Could not load the catalogs for the bulk contracts screen', error);
        } finally {
            Loading.hide();
        }
    }, [t, addToast]);

    const uploadFile = useCallback(async (file: File) => {
        try {
            Loading.show();
            const uploaded = await BulkContractsService.uploadFile(file);
            if (!uploaded?.id) return null;

            uploadedFileIdsRef.current = [...uploadedFileIdsRef.current, uploaded.id];
            setFileNamesById(current => ({ ...current, [uploaded.id]: uploaded.fileName }));
            return { id: uploaded.id, name: uploaded.fileName };
        } catch (error) {
            addToast(t('bulk_contracts.messages.upload_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.CONTRACTS, 'Could not upload the bulk contracts file', error);
            return null;
        } finally {
            Loading.hide();
        }
    }, [addToast, t]);

    const getFileName = useCallback((id: string) => fileNamesById[id], [fileNamesById]);

    const fields = useMemo(
        () => (model && options ? buildContractFields(t, model, options) : null),
        [model, options, t]);

    const rowHook = useMemo(
        () => (model && options ? buildContractRowHook(t, model, options) : undefined),
        [model, options, t]);

    // Nos contratos de aquisição há colunas que não se aplicam ao sub-tipo escolhido (uma compra não
    // tem periodicidade nem data de fim) — ficam bloqueadas, com o motivo em tooltip.
    const isCellEditable = useCallback((row: Data<string>, fieldKey: string) => {
        if (model !== ContractModel.USE || !options) return true;
        return !lockedUseContractFields(useContractTypeNameOf(row, options), row).includes(fieldKey);
    }, [model, options]);

    const getCellLockedReason = useCallback((row: Data<string>, fieldKey: string) => {
        if (isCellEditable(row, fieldKey) || !options) return undefined;
        return t('bulk_contracts.errors.not_applicable_to_type', {
            type: options.contractTypes.find(type => type.value === row.useContractTypeId)?.label ?? '',
        }) as string;
    }, [isCellEditable, options, t]);

    // O `line` é sempre a posição da linha na grelha (a coluna "#"), mesmo quando só uma parte das
    // linhas é enviada — é assim que os avisos e as falhas voltam a apontar para a linha certa.
    const toRequestRows = useCallback((rows: { row: Data<string>; line: number }[]): BulkContractRow[] => rows.map(({ row, line }) => ({
        line,
        vehicleId: toText(row.vehicleId),
        number: toText(row.number),
        supplierId: toText(row.supplierId),
        periodicity: toText(row.periodicity),
        cost: toNumber(row.cost),
        startDate: toIsoDate(row.startDate),
        endDate: toIsoDate(row.endDate),
        contractTypeIds: ((row.contractTypeIds as string) || '').split(MULTI_VALUE_SEPARATOR).map(x => x.trim()).filter(Boolean),
        includeUseContractNumber: toText(row.includeUseContractNumber),
        useContractTypeId: toText(row.useContractTypeId),
        entryValue: toNumber(row.entryValue),
        creditDebitNote: toNumber(row.creditDebitNote),
        kmContractual: toNumber(row.kmContractual),
        warrantyYears: toNumber(row.warrantyYears),
        warrantyKilometers: toNumber(row.warrantyKilometers),
        costPerKmOver: toNumber(row.costPerKmOver),
        costPerKmUnder: toNumber(row.costPerKmUnder),
        kmVehicle: toNumber(row.kmVehicle),
        returnDate: toIsoDate(row.returnDate),
        returnPlace: toText(row.returnPlace),
        stampDutyIncluded: row.stampDutyIncluded === true,
        maintenanceContractIncluded: row.maintenanceContractIncluded === true,
        insuranceIncluded: row.insuranceIncluded === true,
        contractFileId: toText(row.contractFileId),
        maintenanceFileId: toText(row.maintenanceFileId),
        insuranceFileId: toText(row.insuranceFileId),
    })), []);

    // Diz, linha a linha, o que vai acontecer (criar/atualizar) e o que está por resolver. É
    // best-effort: uma falha aqui nunca bloqueia a edição da grelha.
    // Basta o ativo OU o número: só com o número o servidor já identifica o contrato e devolve os
    // dados para preencher a linha. As linhas ainda em branco não vão — o que lhes falta já está
    // assinalado na própria célula e mandá-las só pintava a grelha de avisos.
    const previewRows = useCallback(async (rows: Data<string>[]) => {
        if (!model) return [];

        const toResolve = rows
            .map((row, index) => ({ row, line: index + 1 }))
            .filter(({ row }) => !!row.vehicleId || !!row.number);

        if (!toResolve.length) return [];

        const response = await BulkContractsService.preview({ model, rows: toRequestRows(toResolve) });

        // O anexo que o contrato já tem chega como referência; sem o nome a célula mostrava o id.
        const names: Record<string, string> = {};
        (response?.rows ?? []).forEach(row => {
            if (row.existingValues?.contractFileId && row.existingValues.contractFileName) {
                names[row.existingValues.contractFileId] = row.existingValues.contractFileName;
            }
        });
        if (Object.keys(names).length) {
            setFileNamesById(current => ({ ...names, ...current }));
        }

        return (response?.rows ?? []).map(row => ({
            line: row.line,
            warnings: row.warnings ?? [],
            // Quando o número já corresponde a um contrato, a linha mostra o que lá está — assim vê-se
            // logo que aquele contrato existe e o que se está prestes a alterar.
            values: row.existingValues ? toCellValues(row.existingValues) : undefined,
            valuesKey: row.existingValues?.id,
        }));
    }, [model, toRequestRows]);

    const onSubmit = useCallback((data: Result<string>, _file: File | undefined, onSuccess: () => void, onError: () => void) => {
        if (!model) return;

        // A partir de `all` (todas as linhas da grelha) para que o número da linha nas falhas seja o
        // mesmo que o utilizador vê na coluna "#"; ficam de fora as linhas com erro e as vazias.
        const rows = (data.all ?? [])
            .map((row, index) => ({ row: row as Data<string>, line: index + 1, errors: row.__errors }))
            .filter(({ errors }) => !errors || !Object.values(errors).some(error => error.level === 'error'))
            .filter(({ row }) => !!row.vehicleId);

        if (!rows.length) {
            addToast(t('bulk_contracts.messages.nothing_to_save'), { appearance: 'warning' });
            onError();
            return;
        }

        Loading.show();
        BulkContractsService.commit({ model, rows: toRequestRows(rows), uploadedFileIds: uploadedFileIdsRef.current })
            .then(response => {
                Loading.hide();
                setResult(response);
                if (response.failed?.length) {
                    addToast(t('bulk_contracts.messages.saved_with_failures', { failed: response.failed.length }), { appearance: 'warning' });
                } else {
                    addToast(t('common.messages.record_save_success'), { appearance: 'success' });
                }
                onSuccess();
            })
            .catch(error => {
                Loading.hide();
                addToast(t('common.messages.record_save_error'), { appearance: 'error' });
                Logger.error(LOGGER_LOG_TYPE.CONTRACTS, 'Could not save the contracts in bulk', error);
                onError();
            });
    }, [model, toRequestRows, addToast, t]);

    if (result) {
        return (
            <ScreenTitle title={t('bulk_contracts.title') as string}>
                <CompactPageLayout title={t('bulk_contracts.title') as string} onBack={backToListing}>
                    <BulkContractsResultPanel
                        result={result}
                        onGoToListing={backToListing}
                        onStartOver={() => { setResult(null); backToFamilyStep(); }}
                    />
                </CompactPageLayout>
            </ScreenTitle>
        );
    }

    return (
        <ScreenTitle title={t('bulk_contracts.title') as string}>
            <CompactPageLayout
                title={t('bulk_contracts.title') as string}
                contentAreaClassName={styles.contentArea}
                onBack={onBackFromHeader}
            >
                {!model || !fields || !options
                    ? <ContractFamilyStep onSelect={loadCatalogs} />
                    : <ReactSpreadsheetImport
                        asPage
                        isOpen
                        key={model}
                        fields={fields}
                        rowHook={rowHook}
                        isCellEditable={isCellEditable}
                        getCellLockedReason={getCellLockedReason}
                        showRowNumber
                        startWithBlankRows={BLANK_ROWS}
                        initialFilterByErrors={false}
                        showAttachments={false}
                        ignoreBlankRows
                        enablePaste
                        enableHistory
                        enableFillHandle
                        allowAddRows
                        enableRowActions
                        onUploadFile={uploadFile}
                        getFileName={getFileName}
                        asyncRowValidation={previewRows}
                        validationMessage={t('bulk_contracts.grid_hint') as string}
                        submitButtonText={t('bulk_contracts.save_button') as string}
                        renderToolbarActions={api => (
                            <ReplicateRowsButton
                                vehicles={options.vehicles}
                                rows={api.rows}
                                selectedRowIds={api.selectedRowIds}
                                setRows={api.setRows}
                            />
                        )}
                        onBack={backToFamilyStep}
                        onClose={backToListing}
                        onSubmit={onSubmit}
                    />}
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default BulkContractsScreen;
